<template>
  <component :is="driverData === undefined ? 'div' : 'b-card'">

    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="driverData === undefined"
    >
      <h4 class="alert-heading">
        Error fetching driver data
      </h4>
      <div class="alert-body">
        No Driver found with this driver id. Check
        <b-link
          class="alert-link"
          :to="{ name: 'drivers-list'}"
        >
          Drivers List
        </b-link>
        for other drivers.
      </div>
    </b-alert>
    <b-tabs
      v-if="driverData"
      v-model="tabIndex"
      pills
    >

      <!-- Tab: Account -->
      <b-tab active>
        <template #title>
          <feather-icon
            icon="UserIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Account</span>
        </template>
        <driver-tab-account
          :driver-data="driverData"
          :is-new="isNew"
          class="mt-2 pt-75"
        />
      </b-tab>

      <!-- Tab: Information -->
      <b-tab
        :disabled="isNew"
      >
        <template #title>
          <feather-icon
            icon="InfoIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Information</span>
        </template>
        <driver-edit-tab-information
          v-if="!isNew"
          :driver-data="driverData"
          class="mt-2 pt-75"
          @switch-to-services-tab="tabIndex= $event"
        />
      </b-tab>

      <b-tab
        :disabled="isNew"
      >
        <template #title>
          <feather-icon
            icon="SettingsIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Services</span>
        </template>
        <driver-services-tab
          v-if="!isNew"
          :driver-data="driverData"
          :fetching-data="fetchingData"
          class="mt-2 pt-75"
          @refreshDriver="fetchDriverInfo"
        />
      </b-tab>

      <b-tab
        :disabled="isNew"
      >
        <template #title>
          <feather-icon
            icon="TruckIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Car Info</span>
        </template>
        <driver-tab-car-info
          v-if="!isNew"
          :driver-data="driverData"
          class="mt-2 pt-75"
          @switch-to-documents-tab="tabIndex = $event"
        />
      </b-tab>

      <b-tab
        :disabled="isNew"
      >
        <template #title>
          <feather-icon
            icon="FileIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Documents</span>
        </template>
        <driver-documents-tab
          v-if="!isNew"
          :driver-data="driverData"
          class="mt-2 pt-75"
        />
      </b-tab>
      <b-tab
        :disabled="isNew"
      >
        <template #title>
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Replace Phone</span>
        </template>
        <ReplacePhone />
      </b-tab>
    </b-tabs>
  </component>
</template>

<script>
import {
  BTab, BTabs, BCard, BAlert, BLink,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import driverStoreModule from '../driverStoreModule'
import DriverTabAccount from './DriverTabAccount.vue'
import DriverEditTabInformation from './DriverEditTabInformation.vue'
import DriverTabCarInfo from './DriverTabCarInfo.vue'
import DriverDocumentsTab from './DriverDocumentsTab.vue'
import DriverServicesTab from './DriverServicesTab.vue'
import ReplacePhone from './ReplacePhone.vue'

export default {
  components: {
    BTab,
    BTabs,
    BCard,
    BAlert,
    BLink,

    DriverDocumentsTab,
    ReplacePhone,
    DriverTabAccount,
    DriverEditTabInformation,
    DriverTabCarInfo,
    DriverServicesTab,
  },
  props: {
    isNew: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const driverData = ref(null)
    const fetchingData = ref(false)

    const DRIVER_APP_STORE_MODULE_NAME = 'drivers'

    // Register module
    if (!store.hasModule(DRIVER_APP_STORE_MODULE_NAME)) store.registerModule(DRIVER_APP_STORE_MODULE_NAME, driverStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(DRIVER_APP_STORE_MODULE_NAME)) store.unregisterModule(DRIVER_APP_STORE_MODULE_NAME)
    })
    const tabIndex = ref(0)
    if (props.isNew) {
      driverData.value = {}
    } else {
      fetchingData.value = true
      store.dispatch('drivers/fetchDriver', { id: router.currentRoute.params.id })
        .then(response => {
          const scopedDriverData = response.data.data

          const scopedDriverDataServices = scopedDriverData.services || []
          scopedDriverData.services = scopedDriverDataServices.map(service => ({
            ...service,
            metadata: {
              ...service.metadata,
              constrained_zones: service?.metadata?.constrained_zones ? (service.metadata.constrained_zones.map(zone => ({
                zone_id: zone,
                label: zone,
                value: zone,
              }))) : [],
            },
            chosenConstrainedValue: service?.metadata?.open_for_all_zones ? 'open' : 'constrained',
          }))
          driverData.value = scopedDriverData
          fetchingData.value = false
        })
        .catch(error => {
          if (error?.response?.status === 404) {
            driverData.value = undefined
            fetchingData.value = false
          }
        })
    }

    const fetchDriverInfo = async () => {
      fetchingData.value = true
      store.dispatch('drivers/fetchDriver', { id: router.currentRoute.params.id })
        .then(response => {
          const scopedDriverData = response.data.data
          scopedDriverData.services = scopedDriverData.services.map(service => ({
            ...service,
            metadata: {
              ...service.metadata,
              constrained_zones: service?.metadata?.constrained_zones ? (service.metadata.constrained_zones.map(zone => ({
                zone_id: zone,
                label: zone,
                value: zone,
              }))) : [],
            },
            chosenConstrainedValue: service?.metadata?.open_for_all_zones ? 'open' : 'constrained',
          }))
          driverData.value = scopedDriverData
          fetchingData.value = false
        })
        .catch(error => {
          console.log(error)
          if (error.response.status === 404) {
            driverData.value = undefined
            fetchingData.value = false
          }
        })
    }

    return {
      driverData,
      tabIndex,
      fetchDriverInfo,
      fetchingData,
    }
  },
}
</script>

<style>

</style>
